import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { optimizationPricesResultSelector } from '../../../../reducers/OtimizacaoPrecos';
import {
  ChartAnalysis,
  setAnalysisChart,
  setPeriodBreakFilter,
  setPeriodFilter,
  setShowHowWeCalculateModal,
  setStoresIdFilter,
} from '../../../../reducers/OtimizacaoPrecos/optimizationPricesResult';
import { snakeCaseToCamelCase } from '../../../../utils/snakeCaseToCamelCase';
import { getOptimizationAnalysisProfitChart, getOptimizationAnalysisRevenueChart } from '../services';

export function useResultAnalysis() {
  const { id } = useParams() as Record<'id', string>;
  const dispatch = useDispatch();
  const { tabs, result, header } = useSelector(optimizationPricesResultSelector, shallowEqual);
  const { filters, data } = tabs.analysis;

  const haveResults = tabs.analysis.charts?.profit?.haveResults && tabs.analysis.charts?.revenue?.haveResults;
  const iraAlreadyRun = tabs.analysis.charts?.profit?.iraAlreadyRun && tabs.analysis.charts?.revenue?.iraAlreadyRun;

  const periodBreakLabel = useMemo(() => {
    const selected = data.periodBreakSelected.find(periodBreak => periodBreak.value === filters.periodBreakSelected);
    return selected?.label || '';
  }, [data.periodBreakSelected, filters.periodBreakSelected]);

  const paramsGetOptimizationAnalysisCharts = useMemo(
    () => ({
      id,
      control_store_id: result.controlStore?.storeId || '',
      start_date: filters?.periodSelected?.length ? new Date(filters?.periodSelected?.[0].toISOString()) : new Date(),
      end_date: filters?.periodSelected?.length ? new Date(filters?.periodSelected?.[1].toISOString()) : new Date(),
      optimization_stores_id: filters.storesSelected,
      period_break: periodBreakLabel,
    }),
    [id, result.controlStore?.storeId, filters?.periodSelected, filters.storesSelected, periodBreakLabel],
  );

  const { isFetched: isFetchedRevenueChart, isFetching: isFetchingRevenueChart } = useQuery({
    queryKey: ['optimization-analysis-chart-revenue', id, paramsGetOptimizationAnalysisCharts],
    retry: false,
    keepPreviousData: true,
    enabled: !!id && !!filters.isCompleted,
    queryFn: () => getOptimizationAnalysisRevenueChart(paramsGetOptimizationAnalysisCharts),
    onSuccess(data) {
      const chartResult: Omit<ChartAnalysis, 'series'> & { revenues: ChartAnalysis['series'] } = snakeCaseToCamelCase(data);

      const controlStoreSeries = chartResult?.revenues?.find(s => s.storeId === result.controlStore?.storeId);
      const restSeries = chartResult?.revenues?.filter(s => s.storeId !== result.controlStore?.storeId);
      const series = [controlStoreSeries, ...restSeries].filter(s => s);

      const status: 'success' | 'empty' = chartResult?.dates?.length > 0 ? 'success' : 'empty';
      const dataChart = {
        dates: chartResult.dates,
        status,
        haveResults: chartResult?.haveResults || chartResult?.dates?.length > 0,
        iraAlreadyRun: chartResult?.iraAlreadyRun,
        series:
          series?.map((revenue: any) => {
            const store = result.stores.find(store => store.storeId === revenue.storeId) ||
              result?.controlStore || {
                storeName: 'Não encontrado',
                storeId: 'Não encontrado',
              };
            return {
              accumulated: revenue.accumulated,
              name: store?.storeName,
              storeId: revenue.storeId,
              data: revenue.variationData,
              accumulatedMonetary: revenue.accumulatedMonetary,
              variationDataMonetary: revenue.variationDataMonetary,
            };
          }) || [],
      };

      dispatch(setAnalysisChart({ data: dataChart, name: 'revenue' }));
    },
  });

  const { isFetched: isFetchedProfitChart, isFetching: isFetchingProfitChart } = useQuery({
    queryKey: ['optimization-analysis-chart-profit', id, paramsGetOptimizationAnalysisCharts],
    retry: false,
    keepPreviousData: true,
    enabled: !!id && !!filters.isCompleted,
    queryFn: () => getOptimizationAnalysisProfitChart(paramsGetOptimizationAnalysisCharts),
    onSuccess(data) {
      const chartResult: Omit<ChartAnalysis, 'series'> & { profits: ChartAnalysis['series'] } = snakeCaseToCamelCase(data);

      const controlStoreSeries = chartResult?.profits?.find(s => s.storeId === result.controlStore?.storeId);
      const restSeries = chartResult?.profits?.filter(s => s.storeId !== result.controlStore?.storeId);
      const series = [controlStoreSeries, ...restSeries].filter(s => s);

      const status: 'success' | 'empty' = chartResult?.dates?.length > 0 ? 'success' : 'empty';
      const dataChart = {
        dates: chartResult.dates,
        status,
        haveResults: chartResult?.haveResults || chartResult?.dates?.length > 0,
        iraAlreadyRun: chartResult?.iraAlreadyRun,
        series:
          series?.map((profit: any) => {
            const store = result.stores.find(store => store.storeId === profit.storeId) ||
              result?.controlStore || {
                storeName: 'Não encontrado',
                storeId: 'Não encontrado',
              };
            return {
              accumulated: profit.accumulated,
              name: store?.storeName,
              storeId: profit.storeId,
              data: profit.variationData,
              accumulatedMonetary: profit.accumulatedMonetary,
              variationDataMonetary: profit.variationDataMonetary,
            };
          }) || [],
      };
      dispatch(setAnalysisChart({ data: dataChart, name: 'profit' }));
    },
  });

  const handleSelectBreakPeriod = useCallback(
    (periodBreakSelected: string) => {
      dispatch(setPeriodBreakFilter({ data: periodBreakSelected }));
    },
    [dispatch],
  );

  const handleSelectStoreId = useCallback(
    (storesIdSelected: string[]) => {
      dispatch(setStoresIdFilter({ data: storesIdSelected }));
    },
    [dispatch],
  );

  const handleSelectPeriodFilter = useCallback(
    (dates: Date[] | null) => {
      dispatch(setPeriodFilter({ data: dates }));
    },
    [dispatch],
  );

  const showHowWeCalculateModal = useCallback(() => {
    dispatch(setShowHowWeCalculateModal({ show: true }));
  }, [dispatch]);

  return {
    handlers: {
      handleSelectBreakPeriod,
      handleSelectStoreId,
      handleSelectPeriodFilter,
      showHowWeCalculateModal,
    },
    states: {
      analysis: tabs.analysis,
      stores: result.stores,
      isLoadingRevenueChart: isFetchingRevenueChart,
      isFetchedRevenueChart,
      isLoadingProfitChart: isFetchingProfitChart,
      isFetchedProfitChart,
      controlStore: result.controlStore,
      filtersIsCompleted: filters.isCompleted,
      lastOptimization: result.lastOptimization,
      storesIdWithoutResults: result.storesIdWithoutResults,
      createdAt: result.createdAt,
      haveResults,
      iraAlreadyRun,
      optimizationStatus: {
        start: header.validity.start,
        end: header.validity.end || new Date(),
        active: header.status.active,
      },
    },
  };
}
